import { useEffect, useState } from "react";
import axios from "axios";
import { ProjectDetail } from "@/components/ProjectDetail";
import { proxyImg } from "@/utils/proxyImg";

const API = `${window.location.origin}/api`;

export const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const fetchProjects = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const response = await axios.get(`${API}/projects?_t=${Date.now()}`, { timeout: 30000 });
      setProjects(response.data);
      setFilteredProjects(response.data);
    } catch (error) {
      const status = error?.response?.status || "No response";
      const detail = error?.response?.data?.detail || error?.message || "Unknown error";
      setFetchError(`${status}: ${detail} (API: ${API}/projects)`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleFilter = (category) => {
    setActiveFilter(category);
    if (category === "all") {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter((p) => p.category === category));
    }
  };

  const filters = [
    { key: "all", label: "All Work" },
    { key: "architecture", label: "Architecture" },
    { key: "interior", label: "Interior" },
    { key: "furniture", label: "Furniture" },
  ];

  return (
    <div data-testid="projects-page">
      {/* Header */}
      <header className="page-header-editorial px-16 lg:px-40 bg-[#F5F5F3]" data-testid="projects-header">
        <div className="max-w-screen-2xl mx-auto">
          <span className="label-caps block mb-6">Portfolio</span>
          <h1 className="page-title-editorial">Selected Work</h1>
        </div>
      </header>

      {/* Filters */}
      <section className="py-8 px-16 lg:px-40 border-b border-[#E0E0DE] bg-[#F5F5F3]" data-testid="project-filters">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex flex-wrap gap-2">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => handleFilter(filter.key)}
                data-testid={`filter-${filter.key}`}
                className={`filter-btn ${activeFilter === filter.key ? "active" : ""}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-editorial px-16 lg:px-40 bg-[#F5F5F3]" data-testid="projects-grid">
        <div className="max-w-screen-2xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-32">
              <div className="spinner"></div>
            </div>
          ) : fetchError ? (
            <div className="text-center py-32">
              <p className="text-[#737373] mb-4">Unable to load projects</p>
              <p className="text-sm text-[#999] mb-6">{fetchError}</p>
              <button onClick={fetchProjects} className="btn-editorial">Try Again</button>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-32">
              <p className="text-[#737373]">No projects found</p>
            </div>
          ) : (
            <div className="project-grid grid-cols-1 md:grid-cols-2">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className={`project-item ${index % 3 === 0 ? "md:col-span-2" : ""}`}
                  data-testid={`project-card-${project.id}`}
                  style={{ aspectRatio: index % 3 === 0 ? "21/9" : "16/12" }}
                >
                  <img src={proxyImg(project.images[0])} alt={project.title} loading="lazy" />
                  <div className="project-overlay">
                    <span className="project-number">{String(index + 1).padStart(2, "0")}</span>
                    <h3 className="project-name">{project.title}</h3>
                    <p className="project-location">{project.location}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Project Detail */}
      <ProjectDetail
        project={selectedProject}
        open={!!selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </div>
  );
};
